/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useRef, useState } from "react";
// react-router-dom components

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// Soft UI Dashboard PRO React components
import SoftBadge from "components/SoftBadge";

// Soft UI Dashboard PRO React example components

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Icon from "@mui/material/Icon";
import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import { Button } from "@mui/material";
import Paper from "@mui/material/Paper";
import { styled } from "@mui/system";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

import Row from "views/fixtures-table/components/FixtureRow";
import SoftInput from "components/SoftInput";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import ActionCell from "./components/ActionCell";

// Soft UI Dashboard PRO React context
import { useSoftUIController } from "context";

// sweetalert2 components
import Swal from "sweetalert2";

// Badges
const vlcourtdisabledTemplate = (
  <SoftBadge variant="contained" color="error" size="xs" badgeContent="vl-Court" container />
);
const vlcourtenabledTemplate = (
  <SoftBadge variant="contained" color="success" size="xs" badgeContent="vl-Court" container />
);

import { getUserData, getAuthorizationHeader } from "Utils/helpers";
import { getData, postData, delData } from "Utils/server";

let refreshTimeout = null;
let initializing = false;

let platformSwitches = null;

/**
 *
 */
const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    //backgroundColor: "#f5f5f9",
    backgroundColor: "#000",
    color: "rgba(f, f, f, 1)", //"rgba(0, 0, 0, 0.87)",
    placement: "left",
    maxWidth: 500,
    minWidth: "minContent",
    height: "minContent",
    textAlign: "left",
    fontSize: "0.65erm",
    border: "1px solid #dadde9",
    boxShadow: "0rem 1.25rem 1.6875rem 0rem rgba(0, 0, 0, 0.05)",
    padding: "15px",
    marginLeft: "15px",
  },
}));

/**
 *
 * @param {*} param0
 * @returns
 */
function FixturesTable({
  selectedSport,
  eventType,
  filterSportData,
  autoRefresh,
  refresh,
  communicateWithParent,
}) {
  const abortController = useRef(null);
  const [controller, dispatch] = useSoftUIController();
  const { sports } = controller;
  const userData = getUserData();
  const originalRowsRef = useRef([]);
  const [rows, setRows] = useState([]);
  const [sport, setSport] = useState(0);
  const [forceClose, setForceClose] = useState("");
  const [floatButton, setFloatButton] = useState(false);
  const [searchValue, setSearchValue] = useState(null);
  const [openBackDrop, setOpenBackDrop] = useState(false);

  function getRowIdentity(row) {
    return `${row.id}-${row.provider_id || ""}`;
  }

  function areRowsSameByReference(currentRows, nextRows) {
    if (currentRows.length !== nextRows.length) return false;

    for (let index = 0; index < currentRows.length; index++) {
      if (currentRows[index] !== nextRows[index]) {
        return false;
      }
    }

    return true;
  }

  function setRowsIfChanged(nextRows) {
    setRows((currentRows) => (areRowsSameByReference(currentRows, nextRows) ? currentRows : nextRows));
  }

  function isRegionRowUnchanged(previousRow, nextRow) {
    return (
      previousRow &&
      previousRow.id === nextRow.id &&
      previousRow.name === nextRow.name &&
      previousRow.region === nextRow.region &&
      previousRow.provider === nextRow.provider &&
      previousRow.provider_id === nextRow.provider_id &&
      previousRow.sport === nextRow.sport &&
      previousRow.eventType === nextRow.eventType &&
      previousRow.competitionId === nextRow.competitionId &&
      previousRow.fixturesCount === nextRow.fixturesCount
    );
  }

  function mergeRowsById(nextRows) {
    const previousRowsById = new Map(
      originalRowsRef.current.map((row) => {
        return [getRowIdentity(row), row];
      })
    );

    return nextRows.map((nextRow) => {
      const previousRow = previousRowsById.get(getRowIdentity(nextRow));

      if (!previousRow) {
        return nextRow;
      }

      if (isRegionRowUnchanged(previousRow, nextRow)) {
        return previousRow;
      }

      return {
        ...previousRow,
        ...nextRow,
        fixtures: previousRow.fixtures || nextRow.fixtures,
      };
    });
  }

  function filterRowsBySearch(inputRows, text) {
    const searchText = text ? text.trim().toLowerCase() : "";
    if (!searchText) return inputRows;

    const filteredRows = [];
    for (let index = 0; index < inputRows.length; index++) {
      const row = inputRows[index];
      if (JSON.stringify(row).toLowerCase().indexOf(searchText) > -1) {
        filteredRows.push(row);
      }
    }

    return filteredRows;
  }

  function applyRows(nextRows) {
    originalRowsRef.current = nextRows;
    const filteredRows = filterRowsBySearch(nextRows, searchValue);
    setRowsIfChanged(filteredRows);
  }

  //console.log(" running searchValue: ", searchValue);

  /**
   *
   */
  async function initData() {
    if (!eventType) return;
    if (!selectedSport) return;
    if (!sports) return;
    if (initializing) {
      initializing = false;
      // return;
    }
    if (abortController.current) abortController.current.abort();
    abortController.current = new AbortController();
    initializing = true;
    let refresh = false;
    if (refreshTimeout) {
      clearInterval(refreshTimeout);
      refreshTimeout = null;
      refresh = true;
    }
    //console.log("competitions-table:initData", selectedSport);
    setSport(selectedSport);
    setOpenBackDrop(true);

    let platformId = userData.defaultPlatform.value;
    let sportSelected = sports.find((item) => item.id == selectedSport);
    let getCompetitionsEndpoint = `/api/v1/keys/${platformId}/competitions/${eventType}/${sportSelected.name}`;

    let response = await getData(
      getCompetitionsEndpoint,
      {
        Authorization: getAuthorizationHeader(),
      },
      null,
      abortController.current ? { signal: abortController.current.signal } : null
    );
    if (response != null) {
      //const data = createFixtures(response.data);
      //setDataTableData(data);
      formatData(response.data);
      setOpenBackDrop(false);
    }

    if (refresh && autoRefresh) {
      autoRefreshTimeout();
    }

    let getMasteTemplateEndpoint = `/api/v1/platforms/${platformId}`;
    let responseMasteTemplate = await getData(getMasteTemplateEndpoint, {
      Authorization: getAuthorizationHeader(),
    });
    if (responseMasteTemplate != null) {
      platformSwitches = responseMasteTemplate.data[0];
    }

    initializing = false;
  }

  function formatData(fixtures) {
    let rows = [];
    let regions = fixtures;

    for (let index = 0; index < regions.length; index++) {
      const regionRow = regions[index];
      rows.push(
        createData(
          regionRow.id,
          regionRow.name,
          regionRow.region,
          regionRow.provider,
          regionRow.provider_id,
          regionRow.sport,
          regionRow.eventType,
          regionRow.competitionId,
          regionRow.fixturesCount,
          createFixtures([])
        )
      );
    }

    applyRows(mergeRowsById(rows));
  }

  function createData(
    id,
    name,
    region,
    provider,
    provider_id,
    sport,
    eventType,
    competitionId,
    fixturesCount,
    fixtures
  ) {
    return {
      id,
      name,
      region,
      provider,
      provider_id,
      sport,
      eventType,
      competitionId,
      fixturesCount,
      fixtures,
    };
  }

  function createFixtures(fixtures) {
    const data = {
      columns: [
        { Header: "id", accessor: "fixtureId" },
        { Header: "competition", accessor: "league", Cell: wrapWordCell },
        { Header: "date", accessor: "date", Cell: wrapWordCell },
        { Header: "home", accessor: "home", width: "100" },
        { Header: "away", accessor: "away", width: "100" },
        { Header: "markets", accessor: "markets", Cell: marketsCellRender, width: "80px" },
        {
          Header: "Match Result",
          accessor: "matchresult",
          Cell: matchResultCellRender,
          width: "240px",
        },
        { Header: "action", accessor: "action", Cell: fixtureActions, width: "100px" },
      ],

      rows: [],
    };

    for (let index = 0; index < fixtures.length; index++) {
      try {
        const fixture = fixtures[index];
        if (fixture) {
          let dt = new Date(fixture.fixture.startDate).toLocaleString("en-GB");
          let matchResultMarket = fixture.markets.find((item) => item.type === "match_result");

          data.rows.push(
            createFixtureData(
              fixture.fixture.fixtureId.skill90,
              fixture.provider_id,
              fixture.fixture.sportName,
              fixture.fixture.region,
              fixture.fixture.league,
              eventType,
              dt,
              fixture.fixture.participants[0].name,
              fixture.fixture.participants[1].name,
              fixture.fixture.status,
              fixture.filtered,
              matchResultMarket
                ? {
                    name: matchResultMarket.name,
                    odds: matchResultMarket.odds,
                  }
                : { name: "", odds: null },
              fixture.markets.filter((item) => item.status !== "deleted"),
              [{ vlcourt: false }, { stats: true }, { timeline: true }, { scoreboard: true }]
            )
          );
        }
      } catch (error) {
        console.log(error);
      }
    }

    return data;
  }

  /**
   *
   * @param {*} fixtureId
   * @param {*} provider_id
   * @param {*} sport
   * @param {*} region
   * @param {*} league
   * @param {*} type
   * @param {*} date
   * @param {*} home
   * @param {*} away
   * @param {*} status
   * @param {*} filtered
   * @param {*} matchresult
   * @param {*} markets
   * @param {*} products
   * @returns
   */
  function createFixtureData(
    fixtureId: number,
    provider_id: number,
    sport: string,
    region: string,
    league: string,
    type: string,
    date: string,
    home: string,
    away: string,
    status: string,
    filtered: boolean,
    matchresult: object,
    markets: array,
    products: array
  ) {
    return {
      fixtureId,
      provider_id,
      sport,
      region,
      league,
      type,
      date,
      home,
      away,
      status,
      filtered,
      matchresult,
      markets,
      products,
    };
  }

  function wrapWordCell({ value, ...rest }) {
    let headerName = rest.column.id;
    return <div className={headerName + "-cell"}>{value}</div>;
  }

  /**
   *
   * @param {*} param0
   * @returns
   */
  function marketsCellRender({ value, ...rest }) {
    let marketsLength = value.length;

    return (
      <div className="markets-cell">
        <HtmlTooltip placement="top" title={"Number off all available markets"}>
          {/* <Link to={"#markets-" + rest.row.cells[0].value}>{marketsLength}</Link> */}
          <span>{marketsLength ? marketsLength : 0}</span>
        </HtmlTooltip>
      </div>
    );
  }

  function fixtureActions({ value, ...rest }) {
    return (
      <ActionCell
        id={rest.row.original.fixtureId}
        filtered={rest.row.original.filtered}
        row={rest.row}
        traderView={platformSwitches ? platformSwitches.trader_view : false}
        handleApplyAction={handleSignals}
      />
    );
  }

  /**
   *
   * @param {*} param0
   * @returns
   */
  function matchResultCellRender({ value, ...rest }) {
    const matchResult = value;

    return (
      // <SoftBox display="block" justifyContent="center" alignItems="center" px={3}>
      //   <SoftBox display="flex">
      //     <SoftTypography
      //       style={{
      //         fontSize: "0.85rem",
      //         width: "100%",
      //         textAlign: "center",
      //         backgroundColor: "aliceblue",
      //       }}
      //     >
      //       {matchResult.name}
      //     </SoftTypography>
      //   </SoftBox>
      <SoftBox
        display="flex"
        justifyContent="center"
        alignItems="center"
        px={3}
        className="market-match-result"
      >
        {matchResult.odds ? (
          matchResult.odds.map((odd, index) => {
            return (
              <SoftBox
                key={index + "-" + odd.label}
                display="block"
                lineHeight={0}
                title={odd.previousValue}
                className="odd-holder"
              >
                <SoftBox display="flex">
                  <SoftTypography className="odd-name">{odd.label}</SoftTypography>
                </SoftBox>
                <SoftBox display="flex" lineHeight={0} title={odd.previousValue}>
                  <SoftTypography className={"odd odd-" + odd.status}>
                    {odd.status === "deleted" ? " - " : parseFloat(odd.value).toFixed(2)}
                  </SoftTypography>
                  <SoftBox
                    color={odd.previousValue > odd.value ? "success" : "error"}
                    lineHeight={2}
                    style={{ display: odd.status === "deleted" ? "none" : "" }}
                  >
                    <Icon
                      sx={{ fontWeight: "bold", fontSize: "0.85rem" }}
                      color={odd.previousValue > odd.value ? "success" : "error"}
                    >
                      {odd.previousValue > odd.value ? "keyboard_arrow_up" : "keyboard_arrow_down"}
                    </Icon>
                  </SoftBox>
                  <SoftTypography
                    style={{ display: index == 2 ? "none" : "" }}
                    className="odd-seperator"
                  >
                    |
                  </SoftTypography>
                </SoftBox>
              </SoftBox>
            );
          })
        ) : (
          <SoftBox display="block" lineHeight={0} title="No Match Result available this moment">
            <SoftBox display="flex">
              <SoftTypography style={{ fontSize: "0.85rem", color: "red" }}> - </SoftTypography>
            </SoftBox>
          </SoftBox>
        )}
        {/* </SoftBox> */}
      </SoftBox>
    );
  }

  function filterData() {
    if (filterSportData) {
      //console.log("applyFilterData: ", JSON.stringify(filterSportData));
    } else {
      //console.log("no Filter to apply");
    }
  }

  function autoRefreshTimeout() {
    //console.log("Timeout enabled !");
    refreshTimeout = setInterval(initData, 30000);
  }

  async function handleSignals(e, id, row, filtered) {
    e.preventDefault();
    let provider_id = row.original.provider_id;
    let signal = {
      platform_id: userData.defaultPlatform.value,
      event_id: id,
      provider_id: provider_id,
    };

    Swal.fire({
      title: "We proccess your request! ",
      html: "Wait for few seconds...",
      timerProgressBar: true,
      didOpen: async () => {
        Swal.showLoading();
        let response = null;
        if (filtered) {
          //("signal TO DELETE to send -> ", signal, filtered);
          let getOddsEndpoint = `/api/v1/fixturefilters/${filtered}`;
          response = await delData(getOddsEndpoint, {
            Authorization: getAuthorizationHeader(),
          });
        } else {
          //console.log("signal TO CREATE to send -> ", signal, filtered);
          let getOddsEndpoint = `/api/v1/fixturefilters/`;
          response = await postData(getOddsEndpoint, signal, {
            Authorization: getAuthorizationHeader(),
          });
        }

        if (!response) {
          Swal.clickDeny();
        } else {
          if (response.status === 200) {
            Swal.clickConfirm();
          }
        }
      },
      willClose: () => {},
      allowOutsideClick: false,
    }).then((result) => {
      /* Read more about handling dismissals below */
      if (result.dismiss === Swal.DismissReason.timer) {
        //console.log("I was closed by the timer");
        //routeChange();
      }

      initData();

      if (result.isConfirmed) {
        Swal.fire("Request Sent!", `Live Data sent to the platform`, "success");
      }
      if (result.isDenied) {
        Swal.fire(
          "Error!",
          `Live Data didn't send to the platform. Please try again later!`,
          "error"
        );
      }
    });
  }

  function changeSearchValueHandler(e) {
    e.preventDefault();
    if (e.target.value) setSearchValue(e.target.value);
    else setSearchValue(" ");
  }

  function filterFixtures() {
    initializing = true;
    if (refreshTimeout) {
      clearInterval(refreshTimeout);
      refreshTimeout = null;
      communicateWithParent("resetTimer");
    }

    const filteredRows = filterRowsBySearch(originalRowsRef.current, searchValue);
    setRowsIfChanged(filteredRows);

    if (autoRefresh) {
      autoRefreshTimeout();
      communicateWithParent("startTimer");
    }
    initializing = false;
  }

  async function collapseAll(e) {
    e.preventDefault();
    let now = new Date();
    setForceClose(now.toTimeString());
  }

  useEffect(() => {
    //console.log("listen selectedSport,refresh changes");
    if (refresh) {
      initData();
    }
  }, [refresh]);

  useEffect(() => {
    if (selectedSport) {
      initData();
    }
  }, [selectedSport]);

  useEffect(() => {
    if (eventType) {
      initData();
    }
  }, [eventType]);

  useEffect(() => {
    initData();
    filterData();
  }, [filterSportData]);

  useEffect(() => {
    //console.log("listen autoRefresh changes");
    if (autoRefresh) {
      autoRefreshTimeout();
    } else {
      if (refreshTimeout) {
        clearInterval(refreshTimeout);
        refreshTimeout = null;
      }
    }
    return () => {
      if (refreshTimeout) {
        clearInterval(refreshTimeout);
        refreshTimeout = null;
      }
    };
  }, [autoRefresh]);

  useEffect(() => {
    //if (sports) console.log(sports);
  }, [sports]);

  useEffect(() => {
    if (searchValue !== null) filterFixtures();
  }, [searchValue]);

  useEffect(() => {
    return () => {
      // console.log("living monitoring page...");
      if (abortController.current) {
        // console.log("abortController monitoring page");
        abortController.current.abort();
      }
    };
  }, []);

  return (
    <TableContainer
      component={Paper}
      style={{
        overflow: "unset",
        display: " block",
        height: "calc(100vh - 320px)",
        boxShadow: "none",
      }}
    >
      <div id="fixtures-loader" style={{ zIndex: openBackDrop ? 100 : -1 }}>
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={openBackDrop !== undefined ? openBackDrop : false}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </div>
      <SoftBox
        style={{
          position: "absolute",
          width: "70%",
          backgroundColor: "#fff",
          padding: "24px",
          top: "75px",
        }}
      >
        <SoftInput
          size="small"
          style={{ maxWidth: "150px" }}
          placeholder="Search..."
          onChange={(e) => changeSearchValueHandler(e)}
        ></SoftInput>
      </SoftBox>
      <Table
        aria-label="collapsible table"
        style={{ height: "calc(100vh - 320px)", scrollbarWidth: "none", display: "block" }}
      >
        <TableHead className="fixed-table-header">
          <TableRow>
            <TableCell style={{ width: "60px !important", minWidth: "60px !important" }}>
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            </TableCell>
            <TableCell style={{ minWidth: "100%", width: "100%" }}>Competition</TableCell>
            <TableCell align="right">Fixtures</TableCell>
            <TableCell align="right">
              <Button onClick={(e) => collapseAll(e)}>
                <Icon>unfold_less_outlined_icon </Icon>
              </Button>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody
          id="fixtures-top"
          className="fixed-table-body"
          style={{ maxHeight: `calc(100vh - 320px + ${rows.length * 60})` }}
        >
          <TableRow>
            <TableCell>
              <Table>
                <TableBody>
                  {rows.map((row) => (
                    <Row
                      key={row.id + "-" + row.name}
                      rowData={row}
                      traderView={platformSwitches ? platformSwitches.trader_view : false}
                      forceClose={forceClose}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// Typechecking props for the DataTable
FixturesTable.propTypes = {
  selectedSport: PropTypes.number,
  eventType: PropTypes.string,
  filterSportData: PropTypes.object,
  autoRefresh: PropTypes.bool,
  refresh: PropTypes.number,
  communicateWithParent: PropTypes.func,
};

export default FixturesTable;
